import { useState, useCallback } from 'react';
import { MediaItem } from '../types';

/**
 * Hook for managing video segment loading
 * Only one segment loaded at a time
 * 
 * ✅ IMPORTANT: This hook manages segment URLs only.
 * Video element cleanup is handled by the caller (VideoEditor)
 * using switchVideoSource() utility.
 */
export const useVideoSegmentLoading = () => {
  const [loadedSegmentId, setLoadedSegmentId] = useState<string | null>(null);
  const [loadedSegmentUrl, setLoadedSegmentUrl] = useState<string | null>(null);
  const [isLoadingSegment, setIsLoadingSegment] = useState(false);

  /**
   * Load a video segment
   * Drops any previously loaded segment
   */
  const loadSegment = useCallback(async (segment: MediaItem) => {
    console.log(`📥 Loading segment: ${segment.name}`);
    setIsLoadingSegment(true);

    try {
      // Drop previous segment
      if (loadedSegmentId && loadedSegmentId !== segment.id) {
        console.log(`🗑️ Dropping previous segment: ${loadedSegmentId}`);
        setLoadedSegmentUrl(null);
      }

      // Use segment URL (already signed from backend)
      const segmentUrl = segment.url;
      
      // Update loaded segment
      setLoadedSegmentId(segment.id);
      setLoadedSegmentUrl(segmentUrl);
      
      console.log(`✅ Segment loaded: ${segment.name}`);
      
      return segmentUrl;
    } catch (error) {
      console.error(`❌ Failed to load segment: ${error}`);
      throw error;
    } finally {
      setIsLoadingSegment(false);
    }
  }, [loadedSegmentId]);

  /**
   * Clear loaded segment from memory
   */
  const clearLoadedSegment = useCallback(() => {
    console.log(`🧹 Clearing loaded segment`);
    setLoadedSegmentId(null);
    setLoadedSegmentUrl(null);
  }, []);

  /**
   * Check if a segment is currently loaded
   */
  const isSegmentLoaded = useCallback((segmentId: string): boolean => {
    return loadedSegmentId === segmentId;
  }, [loadedSegmentId]);

  return {
    loadedSegmentId,
    loadedSegmentUrl,
    isLoadingSegment,
    loadSegment,
    clearLoadedSegment,
    isSegmentLoaded,
  };
};
